import { z } from 'zod';
import { AmendPaidSalesDocumentResponse, AuthorizeResponse, CancelCodeStatus, DeliveryListResponse, PosCancelResponse, DepositOptions, PosCheckoutResponse, PosLookupResponse, PosOrderResponse, SalesConversionResponse, SalesDocumentList, SalesDocumentHistoryResponse, SalesDocumentResponse, SalesChecksSchema, } from '@aeris/contracts';
import { request } from "./client.js";
const SalesChecksResponse = z.object({ checks: SalesChecksSchema });
/** A conferência da venda, agora — estoque ou caixa (26/09). */
export const recordSalesCheck = async (id, stage) => SalesChecksResponse.parse(await request(`/api/v1/sales/documents/${id}/checks/${stage}`, {
    method: 'POST',
}));
/** O gerente corrige a hora de uma conferência, com motivo. */
export const correctSalesCheck = async (id, stage, body) => SalesChecksResponse.parse(await request(`/api/v1/sales/documents/${id}/checks/${stage}`, {
    method: 'PUT',
    body,
}));
/**
 * `/api/v1/sales` — orçamento, pedido e venda: um documento com um tipo
 * declarado. Inclui o PDV, que é `/sales/pos/…` e por isso mora aqui.
 *
 * Cada transição é UMA chamada, porque é uma operação só com um conjunto de
 * invariantes: o servidor aloca o número, escreve o estoque e muda o status na
 * mesma transação. O `posCheckout` segue a mesma regra pelo motivo mais duro —
 * quatro chamadas daqui não poderiam ser atômicas, e a rede cair entre a
 * segunda e a terceira deixaria a loja segurando estoque que nunca vendeu.
 */
// One set of endpoints for quotes, orders and sales. ADR-0017 keeps them one
// document with a declared kind, and three clients would advertise three
// domains the product does not have.
//
// Every amount crossing this boundary is a string, for the reason ADR-0016
// gives: a JSON number is an IEEE-754 double, and the guarantee would be gone
// before any of our code saw it.
/**
 * Built field by field, for the same reason as the product one above.
 *
 * Exportado para o teste que confere que TODO campo do filtro chega à URL
 * (F161): `exchangeable` e a faixa de valor ficaram de fora, e a busca da troca
 * passou meses mostrando orçamento sem ninguém perceber que o filtro não ia.
 */
export const salesParams = (query) => {
    const params = new URLSearchParams();
    if (query.branchId !== undefined)
        params.set('branchId', query.branchId);
    if (query.kind !== undefined)
        params.set('kind', query.kind);
    if (query.status !== undefined)
        params.set('status', query.status);
    if (query.customerPartyId !== undefined) {
        params.set('customerPartyId', query.customerPartyId);
    }
    if (query.courierPartyId !== undefined) {
        params.set('courierPartyId', query.courierPartyId);
    }
    if (query.salespersonUserId !== undefined) {
        params.set('salespersonUserId', query.salespersonUserId);
    }
    if (query.search !== undefined && query.search.length > 0) {
        params.set('search', query.search);
    }
    if (query.issuedFrom !== undefined)
        params.set('issuedFrom', query.issuedFrom);
    if (query.issuedTo !== undefined)
        params.set('issuedTo', query.issuedTo);
    if (query.exchangeable !== undefined) {
        params.set('exchangeable', String(query.exchangeable));
    }
    if (query.sold !== undefined)
        params.set('sold', String(query.sold));
    if (query.openQuotes !== undefined) {
        params.set('openQuotes', String(query.openQuotes));
    }
    if (query.hideConverted !== undefined) {
        params.set('hideConverted', String(query.hideConverted));
    }
    // Sem os pedidos da fila da rota de entrega (F266).
    if (query.hideRouteQueue !== undefined) {
        params.set('hideRouteQueue', String(query.hideRouteQueue));
    }
    // Pela forma de pagamento (F238).
    if (query.paymentMethodId !== undefined) {
        params.set('paymentMethodId', query.paymentMethodId);
    }
    if (query.totalMin !== undefined)
        params.set('totalMin', query.totalMin);
    if (query.totalMax !== undefined)
        params.set('totalMax', query.totalMax);
    if (query.page !== undefined)
        params.set('page', String(query.page));
    if (query.pageSize !== undefined) {
        params.set('pageSize', String(query.pageSize));
    }
    if (query.sort !== undefined)
        params.set('sort', query.sort);
    if (query.direction !== undefined)
        params.set('direction', query.direction);
    const encoded = params.toString();
    return encoded.length > 0 ? `?${encoded}` : '';
};
export const fetchSalesDocuments = async (query, signal) => SalesDocumentList.parse(await request(`/api/v1/sales/documents${salesParams(query)}`, { signal }));
/**
 * A pergunta da tela do acerto, montada num lugar só — F189.
 *
 * Exportada para que o teste possa ler o que a tela mandou. É a lição da F161
 * e da F182: um filtro que existe na tela, no contrato e na rota mas não é
 * escrito aqui não dá erro nenhum — ele simplesmente não filtra.
 */
export const deliveryParams = (query) => {
    const params = new URLSearchParams();
    if (query.courierPartyId !== undefined) {
        params.set('courierPartyId', query.courierPartyId);
    }
    if (query.scope !== undefined)
        params.set('scope', query.scope);
    if (query.from !== undefined)
        params.set('from', query.from);
    if (query.to !== undefined)
        params.set('to', query.to);
    if (query.search !== undefined && query.search.trim() !== '') {
        params.set('search', query.search.trim());
    }
    if (query.paymentMethodId !== undefined) {
        params.set('paymentMethodId', query.paymentMethodId);
    }
    if (query.sort !== undefined)
        params.set('sort', query.sort);
    if (query.direction !== undefined)
        params.set('direction', query.direction);
    if (query.page !== undefined)
        params.set('page', String(query.page));
    if (query.pageSize !== undefined) {
        params.set('pageSize', String(query.pageSize));
    }
    return `?${params.toString()}`;
};
/** O que um entregador levou e ainda não acertou (F034). */
export const fetchDeliveries = async (query, signal) => DeliveryListResponse.parse(await request(`/api/v1/sales/deliveries${deliveryParams(query)}`, {
    signal,
}));
export const fetchSalesDocument = async (id, signal) => SalesDocumentResponse.parse(await request(`/api/v1/sales/documents/${id}`, { signal }));
/** Quem mudou o quê, e quando (F147). */
export const fetchSalesDocumentHistory = async (id, signal) => SalesDocumentHistoryResponse.parse(await request(`/api/v1/sales/documents/${id}/history`, { signal }));
export const createSalesDocument = async (input) => SalesDocumentResponse.parse(await request('/api/v1/sales/documents', { method: 'POST', body: input }));
export const updateSalesDocument = async (id, input) => SalesDocumentResponse.parse(await request(`/api/v1/sales/documents/${id}`, {
    method: 'PATCH',
    body: input,
}));
/**
 * Corrigir pedido ou venda já paga (F348): tira peças; o valor abate o que a
 * venda deve e o resto vira vale.
 */
export const amendPaidSalesDocument = async (id, input) => AmendPaidSalesDocumentResponse.parse(await request(`/api/v1/sales/documents/${id}/amend-paid`, {
    method: 'POST',
    body: input,
}));
/**
 * Moves a document.
 *
 * One call for every move, because they are one operation with one set of
 * invariants — the server allocates the number and writes the stock inside the
 * same transaction as the status change.
 */
export const transitionSalesDocument = async (id, input) => SalesDocumentResponse.parse(await request(`/api/v1/sales/documents/${id}/transitions`, {
    method: 'POST',
    body: { reason: null, authorisationId: null, ...input },
}));
/**
 * Pede a alguém com mais autoridade que autorize esta ação (ADR-0026).
 *
 * Não abre sessão: a sessão continua sendo a de quem está operando, e o
 * registro guarda os dois nomes — quem pediu e quem deixou.
 */
export const authorizeAction = async (input) => AuthorizeResponse.parse(await request('/api/v1/auth/authorize', { method: 'POST', body: input }));
/** O código da loja (F294; F363: vale em toda autorização). */
export const authorizeWithCancelCode = async (input) => AuthorizeResponse.parse(await request('/api/v1/auth/authorize-code', {
    method: 'POST',
    body: input,
}));
export const fetchCancelCode = async () => CancelCodeStatus.parse(await request('/api/v1/auth/cancel-code'));
export const saveCancelCode = async (input) => CancelCodeStatus.parse(await request('/api/v1/auth/cancel-code', { method: 'PUT', body: input }));
// --- POS (o PDV) ------------------------------------------------------------
//
// Three calls, and the till makes one of them per scan. Everything else it
// needs — re-pricing on a quantity change, the running total, what is still
// owed, the change due — is answered in the browser by the same pure functions
// the server runs (`@aeris/pricing`, `@aeris/sales`, `@aeris/payments`), so a
// counter is never waiting on a network to see a number it already has the
// inputs for. See docs/features/F016-pos.md.
/**
 * A scan or a typed fragment, priced in the same round trip.
 *
 * `pricing` comes back only when the answer was unambiguous — one match. A
 * barcode always is; a fragment is a person about to choose, and pricing every
 * candidate would be an N+1 to answer a question nobody has asked yet.
 */
export const posLookup = async (query, signal) => {
    const params = new URLSearchParams({ term: query.term });
    if (query.limit !== undefined)
        params.set('limit', String(query.limit));
    if (query.priceListId !== undefined) {
        params.set('priceListId', query.priceListId);
    }
    if (query.variantId !== undefined)
        params.set('variantId', query.variantId);
    return PosLookupResponse.parse(await request(`/api/v1/sales/pos/lookup?${params.toString()}`, { signal }));
};
/**
 * Finishes the sale.
 *
 * One call, because the document, the stock it consumes, the payments and the
 * cash in the drawer are one transaction on the server. Four calls from here
 * could not be: the network drops between the second and the third and the shop
 * is holding stock it never sold.
 */
/**
 * O orçamento do balcão (F034).
 *
 * Uma chamada só que cria o documento e segura a mercadoria — as duas coisas
 * são a mesma transação no servidor, pelo motivo que a rota explica.
 */
export const posQuote = async (input) => SalesDocumentResponse.parse(await request('/api/v1/sales/pos/quote', { method: 'POST', body: input }));
/**
 * A entrega que sai do balcão sem o dinheiro (F101).
 *
 * Uma chamada só, porque o pedido e o despacho são uma transação no servidor:
 * duas chamadas daqui deixariam, quando a segunda falhasse, um pedido em pé
 * que ninguém entregou e nenhum título a cobrar.
 *
 * Nada entra na gaveta aqui. O dinheiro aparece na baixa do título, quando o
 * motoboy volta e diz como o cliente pagou.
 */
export const posOrder = async (input) => PosOrderResponse.parse(await request('/api/v1/sales/pos/orders', { method: 'POST', body: input }));
/**
 * Despacha um pedido: tira do estoque e levanta o título a receber (F034).
 *
 * `courierPartyId` nulo é o cliente levando no balcão.
 */
export const dispatchSalesDocument = async (id, input) => SalesDocumentResponse.parse(await request(`/api/v1/sales/documents/${id}/dispatch`, {
    method: 'POST',
    body: input,
}));
/**
 * O cliente retira o pedido e paga agora (F159).
 *
 * A outra metade do que o caixa faz com um pedido. Devolve a mesma resposta do
 * checkout — documento, pagamentos, acerto e gaveta — porque é o mesmo caminho
 * de dinheiro, sobre um documento que já existia.
 */
export const pickupSalesDocument = async (id, input) => PosCheckoutResponse.parse(await request(`/api/v1/sales/documents/${id}/pickup`, {
    method: 'POST',
    body: input,
}));
/**
 * Pagar agora e retirar depois (F218): o orçamento vira pedido confirmado e
 * pago, e a mercadoria fica separada até o cliente buscar.
 */
export const payAndHoldQuote = async (id, input) => PosCheckoutResponse.parse(await request(`/api/v1/sales/documents/${id}/pay-and-hold`, {
    method: 'POST',
    body: input,
}));
/** O caixa aberto e as formas que servem de sinal (F173). */
export const fetchDepositOptions = async (signal) => DepositOptions.parse(await request('/api/v1/sales/deposit-options', { signal }));
/** Recebe o sinal de um pedido confirmado (F173). */
export const depositSalesDocument = async (id, input) => PosCheckoutResponse.parse(await request(`/api/v1/sales/documents/${id}/deposit`, {
    method: 'POST',
    body: input,
}));
export const posCheckout = async (input) => PosCheckoutResponse.parse(await request('/api/v1/sales/pos/checkout', {
    method: 'POST',
    body: input,
}));
/** Reverses a completed sale: stock back, payments refunded, cash out. */
export const posCancelSale = async (id, input) => PosCancelResponse.parse(await request(`/api/v1/sales/pos/sales/${id}/cancel`, {
    method: 'POST',
    body: input,
}));
/**
 * Corrige a forma de um pagamento já recebido, com a senha do gerente (F319):
 * o errado é estornado e o certo entra com o mesmo valor e a mesma data.
 */
export const correctPaymentMethod = async (documentId, paymentId, input) => SalesDocumentResponse.parse(await request(`/api/v1/sales/documents/${documentId}/payments/${paymentId}/method`, { method: 'POST', body: input }));
export const convertQuote = async (id, version) => SalesConversionResponse.parse(await request(`/api/v1/sales/documents/${id}/convert`, {
    method: 'POST',
    body: { version },
}));
//# sourceMappingURL=sales.js.map